import { addMonths } from "date-fns";

// Maps numerical day indexes (0=Sunday, 1=Monday, ...) to RRULE weekday strings
const WEEKDAY_MAP = {
    0: "SU",
    1: "MO",
    2: "TU",
    3: "WE",
    4: "TH",
    5: "FR",
    6: "SA"
};

const WEEKDAY_NUMBER_MAP = {
    SU: 0,
    MO: 1,
    TU: 2,
    WE: 3,
    TH: 4,
    FR: 5,
    SA: 6
};
// Converts selectedDays and selectedNumber to a RRULE string for weekly recurrence.
export function convertToRRuleString(selectedDays, selectedNumber) {
    if (!Array.isArray(selectedDays) || selectedDays.length === 0) return "";
    const byDay = selectedDays.map((day) => WEEKDAY_MAP[day]).join(",");
    return `FREQ=WEEKLY;INTERVAL=${selectedNumber || 1};BYDAY=${byDay}`;
}

export function timestampToTimeString(timestamp) {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const hours = String(date.getHours()).padStart(2, "0");
    const mins = String(date.getMinutes()).padStart(2, "0");
    return `${hours}:${mins}`;
}

export function getSettingsFromRrule(rrule) {
    const components = rrule.split(";");
    const parsedRule = {
        frequency: "",
        selectedDays: [],
        selectedNumber: 1
    };

    components.forEach((component) => {
        const [key, value] = component.split("=");
        switch (key) {
            case "FREQ":
                if (value === "WEEKLY") {
                    parsedRule.frequency = "weeks";
                } else if (value === "MONTHLY") {
                    parsedRule.frequency = "months";
                } else if (value === "YEARLY") {
                    parsedRule.frequency = "years";
                }
                break;
            case "INTERVAL":
                parsedRule.selectedNumber = parseInt(value, 10);
                break;
            case "BYDAY":
                if (value) {
                    if (value === "NUMBER" || value === "WEEK") {
                        parsedRule.selectedDays = value;
                        break;
                    }
                    const daysArray = value.split(",");
                    daysArray.forEach((day) => {
                        parsedRule.selectedDays.push(WEEKDAY_NUMBER_MAP[day]);
                    });
                    parsedRule.selectedDays.sort((a, b) => a - b);
                }
                break;
            case "UNTIL":
                parsedRule.until = value;
                break;
            case "XOCCUR":
                parsedRule.xoccur = value;
                break;
            default:
                break;
        }
    });

    return parsedRule;
}
export function getDirtyValues(dirtyFields, values) {
    const dirtyValues = Object.keys(dirtyFields).reduce((prev, key) => {
        const value = dirtyFields[key];
        if (!value) {
            return prev;
        }
        const isObject = typeof value === "object";
        const isArray = Array.isArray(value);
        const nestedValue =
            isObject && !isArray
                ? getDirtyValues(value, values[key])
                : values[key];
        return { ...prev, [key]: isArray ? values[key] : nestedValue };
    }, {});
    return dirtyValues;
}

function convertToAPISchema(newSchedule) {
    return {
        startDate: getYYYYMMDD(newSchedule.rruleStartDate),
        endDate: getYYYYMMDD(newSchedule.rruleEndDate),
        rrule: convertToRRuleString(
            newSchedule.numberSelector.selectedDays,
            newSchedule.numberSelector.selectedNumber
        ),
        assignment: newSchedule.rruleTimeBlocks.map((assignment) => {
            return {
                startTime: timestampToTimeString(assignment.startTime),
                providerId: assignment.providerInfo.id,
                providerTimezone: assignment.providerInfo.timezone
            };
        })
    };
}

export function getCourseSchedulesDiff(courseInfo, schedules) {
    function deepEqual(obj1, obj2, excludeKeys = []) {
        const keys1 = Object.keys(obj1).filter(
            (key) => !excludeKeys.includes(key)
        );
        const keys2 = Object.keys(obj2).filter(
            (key) => !excludeKeys.includes(key)
        );

        if (keys1.length !== keys2.length) return false;

        for (let key of keys1) {
            if (!keys2.includes(key)) return false;

            const val1 = obj1[key];
            const val2 = obj2[key];

            if (
                typeof val1 === "object" &&
                val1 !== null &&
                typeof val2 === "object" &&
                val2 !== null
            ) {
                if (Array.isArray(val1) && Array.isArray(val2)) {
                    if (val1.length !== val2.length) return false;
                    for (let i = 0; i < val1.length; i++) {
                        if (!deepEqual(val1[i], val2[i])) return false;
                    }
                } else {
                    if (!deepEqual(val1, val2)) return false;
                }
            } else if (val1 !== val2) {
                return false;
            }
        }
        return true;
    }

    function compareTimeBlocks(original, updated) {
        const assignmentChanges = [];

        // Check for removed items
        original.forEach((origBlock, index) => {
            const found = updated.find(
                (updBlock) =>
                    updBlock.startTime === origBlock.startTime &&
                    updBlock.providerInfo?.id === origBlock.providerInfo?.id
            );
            if (!found) {
                assignmentChanges.push({
                    type: "removed",
                    index: index,
                    item: origBlock
                });
            }
        });

        // Check for added items and modified items
        updated.forEach((updBlock, index) => {
            const origIndex = original.findIndex(
                (origBlock) =>
                    origBlock.startTime === updBlock.startTime &&
                    origBlock.providerInfo?.id === updBlock.providerInfo?.id
            );

            if (origIndex === -1) {
                assignmentChanges.push({
                    type: "added",
                    index: index,
                    item: updBlock
                });
            }
        });

        return assignmentChanges;
    }

    function compareRruleChange(scheduleItem, courseScheduleArray) {
        // TODO: handle deleted schedule box use case
        function isSameDate(date1, date2) {
            // Convert to Date objects if they aren't already
            const d1 = new Date(date1);
            const d2 = new Date(date2);

            // Check if dates are valid
            if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
                return false;
            }

            // Compare year, month, and date
            return (
                d1.getFullYear() === d2.getFullYear() &&
                d1.getMonth() === d2.getMonth() &&
                d1.getDate() === d2.getDate()
            );
        }

        const foundCourseSchedule = courseScheduleArray.find(
            (courseSchedule) =>
                courseSchedule.scheduleId === scheduleItem.scheduleId
        );
        if (foundCourseSchedule) {
            const rruleMatch = deepEqual(
                {
                    frequency: foundCourseSchedule.rrule?.frequency,
                    selectedDays: foundCourseSchedule.rrule?.selectedDays,
                    selectedNumber: foundCourseSchedule.rrule?.selectedNumber
                },
                {
                    frequency: "weeks", // Assuming frequency is always weeks based on your example
                    selectedDays: scheduleItem.numberSelector?.selectedDays,
                    selectedNumber: scheduleItem.numberSelector?.selectedNumber
                }
            );

            // Compare dates (converting schedule format to course format)
            const startDateMatch = isSameDate(
                foundCourseSchedule.startDate,
                scheduleItem.rruleStartDate
            );
            const endDateMatch = isSameDate(
                foundCourseSchedule.endDate,
                scheduleItem.rruleEndDate
            );
            const scheduleMatch = rruleMatch && startDateMatch && endDateMatch;
            if (scheduleMatch)
                return {
                    changeType: "nochange",
                    matchingSchedule: foundCourseSchedule
                };
            else
                return {
                    changeType: "changed",
                    matchingSchedule: foundCourseSchedule
                };
        } else {
            return { changeType: "newschedule" };
        }
    }
    const changes = {
        changedSchedules: [],
        newSchedules: []
    };
    schedules.forEach((scheduleItem, scheduleIndex) => {
        const { changeType: rruleChangeType, matchingSchedule } =
            compareRruleChange(scheduleItem, courseInfo);

        if (rruleChangeType === "newschedule") {
            // This is a new schedule
            changes.newSchedules.push({
                index: scheduleIndex,
                schedule: convertToAPISchema(scheduleItem)
            });
            return;
        } else {
            // rruleChangeType === 'changed' or 'nochange'
            // company assignments now

            // Compare rruleTimeBlocks
            const timeBlockChanges = compareTimeBlocks(
                matchingSchedule.timeBlocks || [],
                scheduleItem.rruleTimeBlocks || []
            );

            let assignmentChanges;
            if (timeBlockChanges.length > 0) {
                assignmentChanges = {
                    scheduleId: matchingSchedule.scheduleId,
                    changes: timeBlockChanges
                };
            }
            if (rruleChangeType === "nochange" && !assignmentChanges)
                return changes;
            else {
                // rrule "changed"
                const changedSchedule = {
                    rruleChanged: rruleChangeType === "changed",
                    rruleChanges:
                        rruleChangeType === "changed"
                            ? {
                                  numberSelector: scheduleItem.numberSelector,
                                  startDate: getYYYYMMDD(
                                      scheduleItem.rruleStartDate
                                  ),
                                  endDate: getYYYYMMDD(
                                      scheduleItem.rruleEndDate
                                  ),
                                  rrule: convertToRRuleString(
                                      scheduleItem.numberSelector.selectedDays,
                                      scheduleItem.numberSelector.selectedNumber
                                  )
                              }
                            : undefined,
                    scheduleId: matchingSchedule.scheduleId,
                    assignmentChanges
                };
                changes.changedSchedules.push(changedSchedule);
            }
        }
    });

    return changes;
}

const getDefaultStartTime = () => {
    const date = new Date();
    date.setHours(9, 0, 0, 0);
    return date.getTime();
};
export const defaultRRule = {
    rruleStartDate: new Date(new Date().setHours(0, 0, 0, 0)),
    rruleEndDate: addMonths(new Date(new Date().setHours(0, 0, 0, 0)), 1),
    rruleSelectedDays: [],
    rruleTimeBlocks: [
        { startTime: getDefaultStartTime(), providerInfo: { id: "", name: "" } }
    ]
};

export function createScheduleDiffObjectForDateRangeDeletion(
    courseScheduleInfo,
    scheduleId
) {
    const tobeDeletedDateRange = courseScheduleInfo.find(
        (schedule) => schedule.scheduleId === scheduleId
    );
    if (tobeDeletedDateRange) {
        const changes = tobeDeletedDateRange.assignment.map((assignment) => {
            const [startDateTimePart] = assignment.startDateTime.split("|");
            const [, time] = startDateTimePart.split("T");
            return {
                type: "removed",
                item: {
                    startTime: time,
                    providerInfo: {
                        id: assignment.providerInfo.id
                    },
                    sequenceId: assignment.sequenceId
                }
            };
        });

        return {
            changedSchedules: [
                {
                    rruleChanged: false,
                    scheduleId,
                    assignmentChanges: {
                        scheduleId,
                        changes
                    }
                }
            ]
        };
    }
}
export const serviceTypeFieldMap = {
    active: "active",
    serviceType: "Classification",
    serviceName: "name",
    categoryName: "categoryName",
    locations: "locations",
    durationType: "durationType",
    duration: "minutes",
    allowRecurring: "allowRecurring",
    TaxOverride: "TaxOverride",
    taxexempt: "taxexempt",
    bookingBehaviour: "behavior",
    price: "price",
    description: "desc"
};

export function validateDateRangeWithInterval(startDate, endDate, interval) {
    if (startDate && endDate && interval) {
        const start = new Date(
            startDate.getFullYear(),
            startDate.getMonth(),
            startDate.getDate(),
            0,
            0,
            0
        );
        const end = new Date(
            endDate.getFullYear(),
            endDate.getMonth(),
            endDate.getDate(),
            23,
            59,
            59
        );

        // Calculate number of weeks
        const timeDiff = end.getTime() - start.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
        return daysDiff < interval * 7
            ? daysDiff < 7
                ? 1
                : Math.floor(daysDiff / 7)
            : interval;
    }
}

export function getYYYYMMDD(dateObj) {
    if (!dateObj) return "";
    const year = dateObj.getFullYear();
    // getMonth() is zero-based (0-11), so we add 1
    const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
    const day = dateObj.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
}
